import React from 'react';
import { useNavigate } from 'react-router-dom';
import { motion } from 'framer-motion';
import { Cpu, CheckCircle, Clock, MessageCircle, ArrowRight, FileText, Star, Award, Shield } from 'lucide-react';
import useSEO from '../utils/useSEO';

const fadeUp = {
  hidden: { opacity: 0, y: 30 },
  visible: (i = 0) => ({ opacity: 1, y: 0, transition: { duration: 0.55, delay: i * 0.1, ease: 'easeOut' } }),
};

const CollegeProjectPage = () => {
  const navigate = useNavigate();

  useSEO({
    title: 'College Project Help | Final Year Project Writing Service | AGO Innovations',
    description:
      'Expert college project help for engineering, medical, and B.Sc students. Final year project reports, documentation, presentations, and research papers — written by experts. Fast delivery.',
    keywords:
      'college project help, final year project help, engineering project writing, project report writing service, final year project documentation, college project file writing, project help India',
    canonical: 'https://agoinnovations.in/college-project-help',
    structuredData: {
      '@context': 'https://schema.org',
      '@type': 'Service',
      name: 'College Project Writing Service',
      provider: { '@type': 'Organization', name: 'AGO Innovations', url: 'https://agoinnovations.in' },
      description: 'Expert college project writing and documentation service for engineering and science students.',
      areaServed: 'IN',
      serviceType: 'Academic Project Writing',
    },
  });

  const services = [
    { icon: <FileText className="w-6 h-6" />, title: 'Final Year Project Report', desc: 'Complete final year project documentation — abstract, introduction, methodology, results, and references.' },
    { icon: <Cpu className="w-6 h-6" />, title: 'Engineering Projects', desc: 'Technical project reports for CSE, Mechanical, Civil, ECE, and Electrical engineering branches.' },
    { icon: <Star className="w-6 h-6" />, title: 'Research Papers', desc: 'Academic research papers, literature reviews, and IEEE-format papers for college submission.' },
    { icon: <Award className="w-6 h-6" />, title: 'Project Presentations', desc: 'Professional PowerPoint presentations for project viva, conferences, and exhibitions.' },
    { icon: <Shield className="w-6 h-6" />, title: 'Synopsis Writing', desc: 'Clear and concise project synopsis and abstract for approvals and pre-submissions.' },
    { icon: <Clock className="w-6 h-6" />, title: 'Mini Projects', desc: 'Mini project reports and documentation for 3rd and 5th semester students.' },
  ];

  const faqs = [
    { q: 'Do you write final year project reports for engineering students?', a: 'Yes. We cover all engineering branches — CSE, Mechanical, Civil, ECE, Electrical, Chemical, and more. Our writers understand technical project documentation standards.' },
    { q: 'What format do you follow for project reports?', a: 'We follow your university/college specific format. Just share the guidelines, and we\'ll match the required structure, referencing style, and page requirements.' },
    { q: 'How long does a project report take?', a: 'A standard final year project report is delivered in 3–7 days. Shorter mini-projects or synopses take 1–3 days. Rush delivery is available.' },
    { q: 'Can you help with presentation slides too?', a: 'Yes. We create professional project presentations for viva and defence sessions along with the written report.' },
    { q: 'Is the project report plagiarism-free?', a: 'Every project report is written from scratch based on your specific project. We don\'t reuse content from existing reports.' },
  ];

  return (
    <div className="min-h-screen bg-white">
      <section className="bg-gradient-to-br from-indigo-900 via-blue-900 to-indigo-800 pt-28 pb-20 px-4 text-white text-center relative overflow-hidden">
        <div className="absolute top-0 left-0 right-0 h-1 bg-gradient-to-r from-yellow-400 via-orange-400 to-yellow-400" />
        <div className="absolute -top-20 -right-20 w-80 h-80 bg-indigo-500/20 rounded-full blur-3xl" />
        <motion.div initial={{ opacity: 0, y: 30 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.7 }} className="relative z-10 max-w-4xl mx-auto">
          <span className="inline-block bg-yellow-400/20 border border-yellow-400/40 text-yellow-300 text-sm font-semibold px-5 py-1.5 rounded-full mb-5">🎓 Student Services — College Projects</span>
          <h1 className="text-4xl md:text-6xl font-black mb-5 leading-tight">College Project Help<span className="block text-yellow-400">for Engineering Students</span></h1>
          <p className="text-indigo-100 text-lg max-w-2xl mx-auto mb-8 leading-relaxed">Final year project reports, research papers, synopses, and presentations — written by technical experts. Get your project documentation done right, on time.</p>
          <div className="flex flex-col sm:flex-row gap-4 justify-center">
            <motion.button whileHover={{ scale: 1.05 }} whileTap={{ scale: 0.97 }} onClick={() => navigate('/learn/customer-work')} className="bg-yellow-400 hover:bg-yellow-300 text-indigo-900 font-bold px-8 py-4 rounded-full shadow-lg transition-colors flex items-center justify-center gap-2">Submit Project Requirement <ArrowRight className="w-5 h-5" /></motion.button>
            <a href="https://wa.me/91XXXXXXXXXX?text=Hi!%20I%20need%20college%20project%20help." target="_blank" rel="noopener noreferrer" className="bg-green-500 hover:bg-green-400 text-white font-bold px-8 py-4 rounded-full shadow-lg transition-colors flex items-center justify-center gap-2"><MessageCircle className="w-5 h-5" /> WhatsApp Us</a>
          </div>
          <div className="flex flex-wrap justify-center gap-6 mt-10 text-sm text-indigo-200">
            <span className="flex items-center gap-2"><CheckCircle className="w-4 h-4 text-green-400" /> All Engineering Branches</span>
            <span className="flex items-center gap-2"><CheckCircle className="w-4 h-4 text-green-400" /> Plagiarism-Free Reports</span>
            <span className="flex items-center gap-2"><CheckCircle className="w-4 h-4 text-green-400" /> Fast Delivery</span>
          </div>
        </motion.div>
      </section>

      <section className="py-20 px-4 bg-gray-50">
        <div className="max-w-5xl mx-auto">
          <motion.div variants={fadeUp} initial="hidden" whileInView="visible" viewport={{ once: true }} className="text-center mb-14">
            <h2 className="text-3xl md:text-4xl font-black text-gray-900 mb-3">College Project Services We Offer</h2>
            <p className="text-gray-500 text-lg">From synopsis to final report — complete project documentation support.</p>
          </motion.div>
          <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-6">
            {services.map((svc, i) => (
              <motion.div key={i} custom={i} variants={fadeUp} initial="hidden" whileInView="visible" viewport={{ once: true }} className="bg-white rounded-2xl p-6 border border-gray-100 shadow-sm hover:shadow-md transition-shadow">
                <div className="w-12 h-12 bg-indigo-50 rounded-xl flex items-center justify-center text-indigo-600 mb-4">{svc.icon}</div>
                <h3 className="font-black text-gray-900 text-base mb-2">{svc.title}</h3>
                <p className="text-gray-500 text-sm leading-relaxed">{svc.desc}</p>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      <section className="py-20 px-4 bg-gray-50">
        <div className="max-w-3xl mx-auto">
          <motion.div variants={fadeUp} initial="hidden" whileInView="visible" viewport={{ once: true }} className="text-center mb-12">
            <h2 className="text-3xl font-black text-gray-900 mb-3">Project Help FAQs</h2>
          </motion.div>
          <div className="space-y-4">
            {faqs.map((faq, i) => (
              <motion.div key={i} custom={i} variants={fadeUp} initial="hidden" whileInView="visible" viewport={{ once: true }} className="bg-white rounded-2xl p-6 border border-gray-100">
                <h3 className="font-bold text-gray-900 mb-2 text-sm">{faq.q}</h3>
                <p className="text-gray-600 text-sm leading-relaxed">{faq.a}</p>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      <section className="py-16 px-4 bg-gradient-to-br from-indigo-800 to-blue-900 text-white text-center">
        <motion.div variants={fadeUp} initial="hidden" whileInView="visible" viewport={{ once: true }} className="max-w-2xl mx-auto">
          <h2 className="text-3xl font-black mb-4">Need College Project Help?</h2>
          <p className="text-indigo-200 mb-8">Submit your project details and get expert documentation delivered on time.</p>
          <div className="flex flex-col sm:flex-row gap-4 justify-center">
            <button onClick={() => navigate('/learn/customer-work')} className="bg-yellow-400 hover:bg-yellow-300 text-indigo-900 font-bold px-8 py-4 rounded-full transition-colors">Submit Requirement</button>
            <button onClick={() => navigate('/school-project-help')} className="bg-white/10 hover:bg-white/20 border border-white/30 text-white font-bold px-8 py-4 rounded-full transition-colors">School Projects</button>
          </div>
        </motion.div>
      </section>
    </div>
  );
};

export default CollegeProjectPage;
